// Import-graph fail-soft recovery for legacy plain placeholders.
// Example: imported `.luis` sources can be rewritten before one retry.
import { rewriteLegacyPlaceholders } from './fail-soft-placeholders'
import type { FailSoftImportRecoveryResult } from './fail-soft-types'
import type { SerializedImportGraph } from './import-resolver/types'

// Build a graph-wide placeholder recovery payload for source and imports.
export function buildLegacyPlaceholderImportRecovery(
  source: string,
  graph: SerializedImportGraph,
  error: unknown,
): FailSoftImportRecoveryResult | null {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  // Check whether the current error is a plain-placeholder syntax failure.
  if (!message.includes('plain loop placeholder')) {
    // Return null for unrelated compile errors.
    return null
  }

  const recoveredSource = rewriteLegacyPlaceholders(source)
  const recoveredGraph = rewriteImportGraph(graph)
  const changedGraph = JSON.stringify(recoveredGraph) !== JSON.stringify(graph)
  // Check whether source or import graph changed after recovery.
  if (recoveredSource === source && !changedGraph) {
    // Return null when no legacy placeholders were rewritten.
    return null
  }

  // Return graph-wide placeholder recovery payload.
  return {
    _source: recoveredSource,
    _line: 1,
    _message: 'Fail-soft recovery rewrote legacy plain `$...` placeholders to `${...}`.',
    _serializedImportGraph: recoveredGraph,
  }
}

// Rewrite every resolved import source in the serialized import graph.
function rewriteImportGraph(graph: SerializedImportGraph): SerializedImportGraph {
  const nextGraph: SerializedImportGraph = {}
  // Copy each source edge-map with rewritten import source text.
  for (const sourceId of Object.keys(graph)) {
    nextGraph[sourceId] = {}
    const requests = graph[sourceId]
    // Copy each request edge with rewritten source payload.
    for (const request of Object.keys(requests)) {
      const resolved = requests[request]
      nextGraph[sourceId][request] = {
        ...resolved,
        source: rewriteLegacyPlaceholders(resolved.source),
      }
    }
  }

  // Return the rewritten serialized import graph.
  return nextGraph
}
